import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Select, Switch, Checkbox } from "antd";
import { Loader2, MessageSquareHeart, Plus, Save, Star } from "lucide-react";
import { ContainerCard } from "../ui/cards";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../common/ToastMsg";
import { DEMO_FEEDBACK_URL, DEMO_FEEDBACK_LOG_URL } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";
import { TEXT, joinSpec, splitSpec, templatePositions } from "./LeadWelcomeWhatsAppCard";

const STATUS_STYLE = {
    pending: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
    sending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
    sent:    'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    skipped: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
    failed:  'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
};
const STATUS_LABEL = { pending: 'Waiting', sending: 'Sending', sent: 'Sent', skipped: 'Not sent', failed: 'Failed' };
const LINK_FIELDS = ['feedback_link', 'feedback_code'];
const DELAYS = [
    { value: 0, label: 'Straight away' }, { value: 30, label: '30 minutes later' },
    { value: 60, label: '1 hour later' }, { value: 120, label: '2 hours later' },
    { value: 240, label: '4 hours later' }, { value: 1440, label: 'Next day' },
];
const LABEL = "text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block";

const fmt = (d) => (d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '');

const formOf = ({ mailboxes, wa_accounts, wa_templates, wa_fields, presenters, ...rest }) =>
    ({ ...rest, wa_variables: rest.wa_variables || {} });

/**
 * Settings → Email Templates: after a lead moves to Demo Done, ask them how the
 * demo went — by email and/or an approved WhatsApp template, each carrying a
 * personal link to the website's feedback page. Backend: email/demo-feedback/
 * (platform_core/email_integration/demo_feedback.py). Off until switched on.
 */
export default function DemoFeedbackCard({ templates, onCreateTemplate }) {
    const { fetch, update } = useApiMethods();
    const navigate = useNavigate();
    const [form, setForm] = useState(null);
    const [options, setOptions] = useState({ mailboxes: [], wa_accounts: [], wa_templates: [], wa_fields: [],
                                             presenters: [] });
    const [channel, setChannel] = useState('email');
    const [log, setLog] = useState([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const take = (data) => {
        setForm(formOf(data));
        setOptions({ mailboxes: data.mailboxes || [], wa_accounts: data.wa_accounts || [],
                     wa_templates: data.wa_templates || [], wa_fields: data.wa_fields || [],
                     presenters: data.presenters || [] });
    };

    useEffect(() => {
        fetch(DEMO_FEEDBACK_URL).then(cfg => {
            if (cfg?.status === 'success') { take(cfg.data); setError(''); }
            else setError(cfg?.message || 'Could not load the demo feedback settings');
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetch(`${DEMO_FEEDBACK_LOG_URL}?channel=${channel}`).then(res => {
            if (res?.status === 'success') setLog(res.data || []);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channel]);

    const fieldKeys = options.wa_fields.map(f => f.key);
    const waTemplate = options.wa_templates.find(t => t.id === form?.wa_template_id);
    const positions = useMemo(() => templatePositions(waTemplate), [waTemplate]);

    const save = async (patch = {}) => {
        // Only the variables this template takes, so a switch of template
        // leaves no stale ones behind.
        const vars = Object.fromEntries(positions.map(p => [p, form.wa_variables[p] || '']));
        setSaving(true);
        const res = await update(DEMO_FEEDBACK_URL, { ...form, wa_variables: vars, ...patch });
        setSaving(false);
        if (res?.status === 'success') {
            take(res.data);
            const which = 'wa_enabled' in patch ? 'WhatsApp' : 'Email';
            const on = patch.is_enabled ?? patch.wa_enabled;
            ToastMsg('success', on === undefined ? 'Saved' : `${which} feedback request switched ${on ? 'on' : 'off'}`);
        } else {
            ToastMsg('error', res?.message || 'Save failed');
        }
    };

    if (error) {
        return <ContainerCard><p className="text-sm text-red-600 dark:text-red-400">{error}</p></ContainerCard>;
    }
    if (!form) {
        return (
            <ContainerCard>
                <div className="flex items-center gap-2 text-sm text-gray-400">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading demo feedback…
                </div>
            </ContainerCard>
        );
    }

    const set = (key) => (v) => setForm(p => ({ ...p, [key]: v ?? null }));
    const setVar = (pos, part) => setForm(p => {
        const cur = splitSpec(p.wa_variables[pos], fieldKeys);
        return { ...p, wa_variables: { ...p.wa_variables, [pos]: joinSpec({ ...cur, ...part }) } };
    });
    const chosen = templates.find(t => t.id === form.template_id);
    const missingLink = chosen && !`${chosen.subject || ''}${chosen.body_html || ''}`.includes('{{feedback_link}}');
    const accountTemplates = options.wa_templates.filter(
        t => !form.wa_account_id || !t.account_id || t.account_id === form.wa_account_id);
    const fieldOptions = [
        ...options.wa_fields.map(f => ({ value: f.key, label: f.label })),
        { value: TEXT, label: 'Fixed text…' },
    ];

    return (
        <ContainerCard>
            <div className="flex items-start gap-3 mb-4">
                <div className="w-9 h-9 rounded-lg bg-brand-50 dark:bg-brand-900/30 flex items-center justify-center shrink-0">
                    <MessageSquareHeart className="h-4 w-4 text-brand-600 dark:text-brand-300" />
                </div>
                <div>
                    <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Demo feedback request</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                        When a lead moves to Demo Done, they get a personal link to rate the demo and tell you what's next.
                        Their answers land on the lead page and the timeline, and the owner is notified, along with managers
                        when the rating is 2 stars or less. Each lead is asked once per channel; reps can send it again from the lead page.
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className={LABEL}>Feedback page URL</label>
                    <Input value={form.page_url || ''} placeholder="https://emedhub.in/feedback/"
                           onChange={(e) => set('page_url')(e.target.value)} />
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">Each lead's code is added to the end.</p>
                </div>
                <div>
                    <label className={LABEL}>When to send</label>
                    <Select className="w-full" value={form.delay_minutes} options={DELAYS}
                            aria-label="When to send" onChange={set('delay_minutes')} />
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">Counted from the move to Demo Done.</p>
                </div>
            </div>

            <section className="mt-5 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">Demo presenter</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                    Who takes your demos, when it isn't the lead's owner. Every move to Demo Fixed or Demo Done is
                    credited to them, the feedback page asks about them, and they get the feedback too.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                    <div>
                        <label className={LABEL}>Default presenter</label>
                        <Select className="w-full" placeholder="The lead owner (no default)" allowClear showSearch
                                optionFilterProp="label" aria-label="Default presenter"
                                value={form.default_presenter_id ?? undefined}
                                options={options.presenters.map(p => ({ value: p.id, label: p.name }))}
                                onChange={set('default_presenter_id')} />
                    </div>
                    <Checkbox className="md:mt-6" checked={form.presenter_choice}
                              onChange={(e) => set('presenter_choice')(e.target.checked)}>
                        <span className="text-xs text-gray-600 dark:text-gray-300">
                            Let reps choose a different presenter on the status form
                        </span>
                    </Checkbox>
                </div>
                <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-2">
                    {form.default_presenter_id && !form.presenter_choice
                        ? "Reps don't pick anyone: every demo goes to the default presenter."
                        : "Reps see a \"Demo by\" picker when they move a lead to Demo Fixed or Demo Done."}
                    {' '}Use {'{{demo_presenter}}'} in the email, or <b>Demo presenter</b> for a WhatsApp variable.
                </p>
            </section>

            <div className="mt-5 grid grid-cols-1 xl:grid-cols-2 gap-4">
                <section className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Email</h3>
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600 dark:text-gray-300">{form.is_enabled ? 'On' : 'Off'}</span>
                            <Switch checked={form.is_enabled} loading={saving} aria-label="Email feedback request"
                                    onChange={(v) => save({ is_enabled: v })} />
                        </div>
                    </div>
                    <div className="flex items-center justify-between mb-1">
                        <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Template</label>
                        {onCreateTemplate && (
                            <button type="button" onClick={onCreateTemplate}
                                    className="flex items-center gap-1 text-xs font-medium text-brand-600 dark:text-brand-300 hover:underline">
                                <Plus className="h-3 w-3" /> New template
                            </button>
                        )}
                    </div>
                    <Select className="w-full" placeholder="Choose a template" allowClear showSearch
                            optionFilterProp="label" value={form.template_id ?? undefined}
                            options={templates.map(t => ({ value: t.id, label: t.name }))}
                            onChange={set('template_id')} />
                    {missingLink && (
                        <p className="text-[11px] text-red-600 dark:text-red-400 mt-1">
                            This template has no {'{{feedback_link}}'} — add it where the button or link should go.
                        </p>
                    )}
                    {!missingLink && (
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                            Put {'{{feedback_link}}'} in the template; {'{{first_name}}'} and {'{{sales_rep_name}}'} work too.
                        </p>
                    )}
                    <label className={cn(LABEL, "mt-3")}>Send from</label>
                    <Select className="w-full" placeholder="Choose a mailbox" allowClear
                            value={form.sender_account_id ?? undefined}
                            options={options.mailboxes.map(m => ({
                                value: m.id, disabled: !m.can_send,
                                label: `${m.email_address}${m.owner ? ` (${m.owner})` : ''}${m.can_send ? '' : ' - SMTP not verified'}`,
                            }))}
                            onChange={set('sender_account_id')} />
                    <Checkbox className="mt-2" checked={form.send_from_owner}
                              onChange={(e) => set('send_from_owner')(e.target.checked)}>
                        <span className="text-xs text-gray-600 dark:text-gray-300">
                            Use the lead owner's own mailbox when they have one
                        </span>
                    </Checkbox>
                </section>

                <section className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">WhatsApp</h3>
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600 dark:text-gray-300">{form.wa_enabled ? 'On' : 'Off'}</span>
                            <Switch checked={form.wa_enabled} loading={saving} aria-label="WhatsApp feedback request"
                                    onChange={(v) => save({ wa_enabled: v })} />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className={LABEL}>Send from</label>
                            <Select className="w-full" placeholder="WhatsApp account" allowClear
                                    value={form.wa_account_id ?? undefined}
                                    options={options.wa_accounts.map(a => ({
                                        value: a.id, label: `${a.name}${a.phone_number ? ` (${a.phone_number})` : ''}`,
                                    }))}
                                    onChange={set('wa_account_id')} />
                        </div>
                        <div>
                            <label className={LABEL}>Template</label>
                            <Select className="w-full" placeholder="Approved template" allowClear showSearch
                                    optionFilterProp="label" value={form.wa_template_id ?? undefined}
                                    options={accountTemplates.map(t => ({ value: t.id, label: `${t.name} · ${t.category}` }))}
                                    notFoundContent={<span className="text-xs text-gray-500">No approved templates - sync them under Settings → WA Templates</span>}
                                    onChange={set('wa_template_id')} />
                        </div>
                    </div>
                    {waTemplate && (
                        <div className="mt-3 space-y-2">
                            <p className="text-sm text-gray-800 dark:text-gray-100 whitespace-pre-wrap rounded-lg bg-green-50/60 dark:bg-green-900/10 border border-green-100 dark:border-green-900/40 p-2.5">
                                {waTemplate.body_text}
                            </p>
                            {positions.map(pos => {
                                const v = splitSpec(form.wa_variables[pos], fieldKeys);
                                return (
                                    <div key={pos} className="flex items-center gap-2">
                                        <span className="w-10 text-xs font-mono text-gray-500">{`{{${pos}}}`}</span>
                                        <Select className="flex-1 min-w-0" placeholder="Fill with…" value={v.field || undefined}
                                                options={fieldOptions} aria-label={`Variable ${pos}`}
                                                onChange={(f) => setVar(pos, { field: f, text: f === TEXT ? v.text : '' })} />
                                        {v.field === TEXT ? (
                                            <Input className="flex-1" value={v.text} placeholder="Text to send"
                                                   onChange={(e) => setVar(pos, { text: e.target.value })} />
                                        ) : ![...LINK_FIELDS, ''].includes(v.field) && (
                                            <Input className="flex-1" value={v.fallback} placeholder="If blank, send…"
                                                   onChange={(e) => setVar(pos, { fallback: e.target.value })} />
                                        )}
                                    </div>
                                );
                            })}
                            <p className="text-[11px] text-gray-500 dark:text-gray-400">
                                For a button whose URL ends in a variable (…/feedback/{'{{2}}'}) choose <b>Demo feedback code (button)</b>.
                                A Utility template is not held back by Meta's marketing limits.
                            </p>
                        </div>
                    )}
                </section>
            </div>

            <div className="flex justify-end mt-4">
                <Button onClick={() => save()} disabled={saving} className="text-sm flex items-center gap-2">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
                </Button>
            </div>

            <div className="mt-6">
                <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Recent requests</h3>
                    <div className="flex rounded-lg border border-gray-200 dark:border-gray-700 p-0.5 text-xs" role="tablist">
                        {[['email', 'Email'], ['whatsapp', 'WhatsApp']].map(([key, label]) => (
                            <button key={key} type="button" role="tab" aria-selected={channel === key}
                                    onClick={() => setChannel(key)}
                                    className={cn("px-2.5 py-1 rounded-md font-medium",
                                        channel === key ? "bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-200"
                                            : "text-gray-500 dark:text-gray-400")}>
                                {label}
                            </button>
                        ))}
                    </div>
                </div>
                {log.length === 0 ? (
                    <p className="text-xs text-gray-400">No feedback requests yet.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left border-b border-gray-200 dark:border-gray-700">
                                    {['Lead', 'Status', 'To', 'Rating', 'When'].map(h => (
                                        <th key={h} className="pb-2 pr-4 text-xs font-semibold text-gray-500 dark:text-gray-400">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                                {log.map(r => (
                                    <tr key={r.id}>
                                        <td className="py-2 pr-4">
                                            <button type="button" className="text-brand-600 dark:text-brand-300 hover:underline text-left"
                                                    onClick={() => navigate(`/customers/customerdetails?id=${r.customer_id}`,
                                                        { state: { formEdit: true, row: { id: r.customer_id } } })}>
                                                {r.customer_name || `Lead #${r.customer_id}`}
                                            </button>
                                        </td>
                                        <td className="py-2 pr-4">
                                            <span className={cn("px-2 py-0.5 text-[10px] font-medium rounded-full", STATUS_STYLE[r.status])}>
                                                {STATUS_LABEL[r.status] || r.status}
                                            </span>
                                            {r.reason && <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">{r.reason}</span>}
                                        </td>
                                        <td className="py-2 pr-4 text-xs text-gray-600 dark:text-gray-300">{r.sent_to || '—'}</td>
                                        <td className="py-2 pr-4 text-xs">
                                            {r.rating ? (
                                                <span className="inline-flex items-center gap-0.5 font-medium text-amber-600 dark:text-amber-400">
                                                    <Star className="h-3 w-3 fill-current" aria-hidden="true" /> {r.rating}/5
                                                </span>
                                            ) : <span className="text-gray-400">—</span>}
                                        </td>
                                        <td className="py-2 text-xs text-gray-500 dark:text-gray-400">{fmt(r.sent_at || r.created_date)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </ContainerCard>
    );
}
